import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { Activity, ChevronLeft, ChevronRight, LogOut, RefreshCw, Shield } from "lucide-react";
import { BondLockup } from "../components/BondMark";
import ADMIN_THEME from "./theme";
import { AdminLayoutContext } from "./AdminLayoutContext";
import { getNavGroups } from "./navConfig";
import NavIcon from "./components/NavIcon";
import { usePersistedPanelSize } from "./hooks/usePersistedPanelSize";

const SIDEBAR_DEFAULT = 248;
const SIDEBAR_MIN = 68;
const SIDEBAR_MAX = 380;
const SIDEBAR_COMPACT = 120;

function initials(name) {
  if (!name) return "AD";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "AD";
}

function relativeTime(ts) {
  if (!ts) return "—";
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

/** Attach live stats from overview to nav items where relevant. */
function enrichItem(item, overview) {
  const o = overview || {};
  const enriched = { ...item };

  if (item.id === "system") {
    enriched.statusDot = true;
    enriched.statusDotOk = Boolean(o.system?.redis);
  }

  if (item.id === "p2p" && o.p2p?.pendingOutcome > 0) {
    enriched.stat = o.p2p.pendingOutcome;
    enriched.statTone = "alert";
  }

  const pendingPayments = (o.payments?.pendingWithdrawals || 0) + (o.payments?.pendingDeposits || 0);
  if (item.id === "transaction" && pendingPayments > 0) {
    enriched.stat = pendingPayments;
    enriched.statTone = "warn";
  }

  if (item.id === "users" && o.users?.banned > 0) {
    enriched.stat = o.users.banned;
    enriched.statTone = "default";
  }

  return enriched;
}

function NavItem({ item, active, compact, onNavigate }) {
  const tooltip = compact
    ? [item.label, item.hint].filter(Boolean).join(" — ")
    : item.description || item.label;

  return (
    <button
      type="button"
      className={`admin-nav-item ${active ? "is-active" : ""}`}
      onClick={() => onNavigate(item.id)}
      title={tooltip}
    >
      <span className="admin-nav-indicator" aria-hidden />
      <span className="admin-nav-icon">
        <NavIcon id={item.id} />
      </span>
      <span className="admin-nav-copy">
        <span className="admin-nav-label">{item.label}</span>
        {item.hint ? <span className="admin-nav-hint">{item.hint}</span> : null}
      </span>
      {item.statusDot ? (
        <span
          className={`admin-nav-dot${item.statusDotOk ? " is-live" : " is-down"}`}
          aria-hidden
        />
      ) : null}
      {item.stat != null && item.stat !== "" ? (
        <span className={`admin-nav-stat tone-${item.statTone || "default"}`}>{item.stat}</span>
      ) : null}
    </button>
  );
}

export default function AdminShell({
  user,
  userRole,
  activeSection,
  onNavigate,
  onLogout,
  overview,
  overviewLoading,
  lastFetchedAt,
  onRefreshOverview,
  children,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const { size: sidebarWidth, setClampedSize, reset: resetSidebarWidth } = usePersistedPanelSize(
    "bb-admin-sidebar-w",
    { defaultSize: SIDEBAR_DEFAULT, minSize: SIDEBAR_MIN, maxSize: SIDEBAR_MAX },
  );

  const isCompact = sidebarWidth <= SIDEBAR_COMPACT;

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    document.body.style.setProperty("--admin-sidebar-w", `${sidebarWidth}px`);
    document.body.classList.toggle("admin-sidebar-compact", isCompact);
    return () => {
      document.body.classList.remove("admin-sidebar-compact");
      document.body.style.removeProperty("--admin-sidebar-w");
    };
  }, [sidebarWidth, isCompact]);

  const onSidebarResizePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const startX = event.clientX;
      const startSize = sidebarWidth;
      document.body.classList.add("admin-is-resizing");

      const onMove = (ev) => {
        setClampedSize(startSize + (ev.clientX - startX));
      };
      const onUp = () => {
        document.body.classList.remove("admin-is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [sidebarWidth, setClampedSize],
  );

  const toggleSidebarWidth = useCallback(() => {
    if (isCompact) resetSidebarWidth();
    else setClampedSize(SIDEBAR_MIN);
  }, [isCompact, resetSidebarWidth, setClampedSize]);

  const isSuper = userRole === "super_admin";
  const systemOk = Boolean(overview?.system?.redis);
  const statusLive = !overviewLoading && systemOk;
  const statusLabel = overviewLoading ? "Syncing" : systemOk ? "Online" : "Degraded";

  const navGroups = useMemo(
    () =>
      getNavGroups(userRole).map((group) => ({
        ...group,
        items: group.items.map((item) => enrichItem(item, overview)),
      })),
    [userRole, overview],
  );

  const sidebarInner = (
    <>
      <header className="admin-sidebar-head">
        <div className="admin-sidebar-brand" aria-label="BetBond admin">
          <BondLockup size={22} textClassName="admin-sidebar-brand-lockup" />
          <span className="admin-sidebar-brand-copy">
            <span className="admin-sidebar-brand-tag">
              <span
                className={`admin-sidebar-pip ${
                  statusLive ? "admin-sidebar-pip-live" : overviewLoading ? "admin-sidebar-pip-idle" : "admin-sidebar-pip-down"
                }`}
                aria-hidden
              />
              {statusLabel}
            </span>
          </span>
        </div>
        <button
          type="button"
          className="admin-sidebar-collapse"
          onClick={toggleSidebarWidth}
          aria-label={isCompact ? "Expand sidebar" : "Collapse sidebar"}
          title={isCompact ? "Expand" : "Collapse"}
        >
          {isCompact ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </header>

      <nav className="admin-sidebar-scroll" aria-label="Admin sections">
        {navGroups.map((group) => (
          <div key={group.id} className="admin-nav-group">
            {group.label ? <p className="admin-nav-group-label">{group.label}</p> : null}
            <div className="admin-sidebar-nav">
              {group.items.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  active={activeSection === item.id}
                  compact={isCompact}
                  onNavigate={(id) => {
                    onNavigate(id);
                    setMobileOpen(false);
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="admin-sidebar-foot">
        <div className="admin-sidebar-account">
          <span className="admin-sidebar-account-avatar">{initials(user?.name)}</span>
          <span className="admin-sidebar-account-copy">
            <span className="admin-sidebar-account-name">{user?.name || "Admin"}</span>
            <span className="admin-sidebar-account-meta">
              {isSuper ? "Super Admin" : "Admin"}
            </span>
          </span>
          <button
            type="button"
            onClick={onRefreshOverview}
            disabled={overviewLoading}
            className="admin-sidebar-action admin-sidebar-action-sync"
            title={`Sync · ${relativeTime(lastFetchedAt)}`}
            aria-label="Sync admin data"
          >
            <RefreshCw size={14} strokeWidth={2.2} className={overviewLoading ? "admin-sync-spin" : ""} />
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="admin-sidebar-action admin-sidebar-action-logout"
            title="Logout"
            aria-label="Logout"
          >
            <LogOut size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="admin-sidebar-foot-meta">
          <span className="admin-sidebar-foot-meta-item">
            <Shield size={10} aria-hidden />
            {isSuper ? "Super Admin" : "Admin"}
          </span>
          <span className="admin-sidebar-foot-meta-item">
            <Activity size={10} aria-hidden /> Command desk
          </span>
        </div>
      </div>
    </>
  );

  return (
    <AdminLayoutContext.Provider value={{ openMobileMenu: () => setMobileOpen(true) }}>
      <div
        className="admin-console flex min-h-screen w-full antialiased"
        style={{
          background: ADMIN_THEME.shell.pageBg,
          "--admin-sidebar-w": `${sidebarWidth}px`,
        }}
      >
        {mobileOpen && (
          <button
            type="button"
            className="admin-sidebar-backdrop lg:hidden"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
        )}

        <div className="admin-sidebar-resize-wrap" style={{ width: sidebarWidth }}>
          <aside
            className={`admin-sidebar admin-sidebar-drawer flex shrink-0 flex-col ${
              isCompact ? "is-compact" : ""
            } ${mobileOpen ? "is-open" : ""}`}
          >
            {sidebarInner}
          </aside>
          <div
            className="admin-sidebar-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={sidebarWidth}
            aria-valuemin={SIDEBAR_MIN}
            aria-valuemax={SIDEBAR_MAX}
            aria-label="Resize navigation"
            title="Drag to resize · double-click to reset"
            onPointerDown={onSidebarResizePointerDown}
            onDoubleClick={resetSidebarWidth}
          />
        </div>

        <div className="admin-main flex min-h-screen min-w-0 flex-1 flex-col">
          <main className="admin-main-content flex-1">{children}</main>
        </div>
      </div>
    </AdminLayoutContext.Provider>
  );
}
